
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { ApolloError } from 'apollo-client/errors/ApolloError'
import BannerFarm from '@mg/web-components/Banners/BannerFarm'
import Banners from '@mg/web-components/Banners/Basic'
import Carousel from '@mg/web-components/Banners/Carousel'
import HpBanner from '@mg/web-components/Banners/HpBanner'
import InspoBanner from '@mg/web-components/Banners/InspoBanner'
import TopIconsBanner from '@mg/web-components/Banners/TopIconsBanner'
import UspBanner from '@mg/web-components/Banners/UspBanner'
import BasicTitle from '@mg/web-components/Base/Typography/BasicTitle'
import DailyDealBanner from './DailyDealBanner.vue'
import CmsCarouselWithImage from '~/components/banners/CmsCarouselWithImage.vue'
import {
  GetPositionedPageBannersQuery,
  GET_POSITIONED_PAGE_BANNERS,
} from '~/graphql/gql/banners'
import {
  GQLPageBanner,
  GQLPageBannerHpBanner,
} from '~/graphql/__generated__/interfaces'
import { getRouteDefinition } from '~/utils/routesGetDefinition'

@Component<BannerWrapper>({
  apollo: {
    getPositionedPageBannersPrefetch: {
      fetchPolicy: 'cache-only',
      query: GET_POSITIONED_PAGE_BANNERS,
      update(data: {
        getPositionedPageBanners: GQLPageBanner[]
      }): GQLPageBanner[] {
        this.isLoaded()

        return data.getPositionedPageBanners
      },
      variables(): GetPositionedPageBannersQuery {
        const routeDef = getRouteDefinition(this.$route)
        const positions =
          this.$shopConfig?.isBot && routeDef?.bannerPrefetchPositionsBot
            ? routeDef.bannerPrefetchPositionsBot
            : routeDef?.bannerPrefetchPositions || []
        const pageUrl = this.$route.path

        return {
          isMobile: this.isMobile,
          pageUrl,
          query: {
            positions,
          },
        }
      },
    },
    getPositionedPageBanner: {
      error(error: ApolloError) {
        this.isLoadedClient()
        this.isLoadedClientPositon()
        this.error = error
      },
      skip(): boolean {
        return this.bannerIsPrefetch
      },
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      query: GET_POSITIONED_PAGE_BANNERS,
      update(data: {
        getPositionedPageBanners: GQLPageBanner[]
      }): GQLPageBanner {
        this.isLoaded()
        this.isLoadedClient()
        this.isLoadedClientPositon()
        return data.getPositionedPageBanners[0] ?? {}
      },
      variables(): GetPositionedPageBannersQuery {
        const pageUrl = this.$route.path
        return {
          isMobile: this.isMobile,
          pageUrl,
          query: {
            positions: [this.position],
          },
        }
      },
    },
  },
  components: {
    BannerFarm,
    Banners,
    BasicTitle,
    Carousel,
    DailyDealBanner,
    CmsCarouselWithImage,
    HpBanner,
    InspoBanner,
    TopIconsBanner,
    UspBanner,
  },
})
export default class BannerWrapper extends Vue {
  @Prop({ required: true, type: Number }) position: number
  @Prop(Boolean) compact: boolean

  error: ApolloError | null = null
  getPositionedPageBanner: GQLPageBanner | null = null
  getPositionedPageBannersPrefetch: GQLPageBanner[]

  get banner() {
    if (this.getPositionedPageBanner) {
      return this.getPositionedPageBanner
    }

    if (!this.getPositionedPageBannersPrefetch) {
      return null
    }

    const banner = this.getPositionedPageBannersPrefetch.find(
      (banner) => banner.position === this.position
    ) as GQLPageBanner

    return banner || null
  }

  get bannerType() {
    return this.banner?.__typename?.replace('PageBanner', '') || ''
  }

  @Emit()
  isLoaded() {}

  @Emit()
  isLoadedClient() {}

  @Emit()
  isLoadedClientPositon() {}

  get isBot() {
    return this.$shopConfig?.isBot
  }

  get componentType() {
    const { bannerType } = this
    const genericTypes = ['SlimBanner', 'Banners']
    const specificTypes = [
      'BannerFarm',
      'Carousel',
      'UspBanner',
      'HpBanner',
      'TopIconsBanner',
      'DailyDealBanner',
      'CmsCarouselWithImage',
      'InspoBanner',
    ]

    if (genericTypes.includes(bannerType)) {
      return 'Banners'
    }
    if (specificTypes.includes(bannerType)) {
      return bannerType
    }
    return null
  }

  get device() {
    return this.$resize.isDesktop ? 'desktop' : 'mobile'
  }

  get isMobile() {
    return this.$resize.isMobile
  }

  get showHeader() {
    return (
      this.banner?.bannerAttributes?.header &&
      this.componentType !== 'BannerFarm' &&
      this.componentType !== 'CmsCarouselWithImage'
    )
  }

  get headerStyle() {
    return {
      ...(this.banner?.bannerAttributes?.headerColor && {
        color: this.banner.bannerAttributes.headerColor,
      }),
      textAlign: this.banner?.bannerAttributes?.headerCentered
        ? 'center'
        : 'left',
    }
  }

  get showTopIcons() {
    return (
      this.componentType === 'HpBanner' &&
      !!(this.banner as GQLPageBannerHpBanner)?.topIcons
    )
  }

  get bannerIsPrefetch() {
    const routeDef = getRouteDefinition(this.$route)
    const positions =
      this.$shopConfig?.isBot && routeDef?.bannerPrefetchPositionsBot
        ? routeDef.bannerPrefetchPositionsBot
        : routeDef?.bannerPrefetchPositions || []

    return !!positions.find((position: number) => position === this.position)
  }
}
